// Schema of tenants/<slug>/business.json — the single file that describes a
// studio. Shared by the tenant pipeline (Node) and the owner cabinet.
import { z } from 'zod';

const time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$|^24:00$/, 'время в формате ЧЧ:ММ');
const range = z
  .string()
  .regex(/^\d{2}:\d{2}-\d{2}:\d{2}$/, 'интервал вида 09:00-21:00')
  .refine((v) => {
    const [a, b] = v.split('-');
    return time.safeParse(a).success && time.safeParse(b).success && b > a;
  }, 'закрытие должно быть позже открытия');
const dayHours = z.union([z.null(), range, z.array(range).max(3)]);
const key = z.string().regex(/^[a-z0-9][a-z0-9_-]{0,39}$/, 'латиница, цифры, - и _');
const imageRef = z.string().regex(/^images\/[A-Za-z0-9._-]+\.(jpe?g|png|webp)$/i, 'путь вида images/имя.jpg');

export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

export const businessSchema = z
  .object({
    $schema: z.string().optional(),
    slug: z.string().regex(/^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/, 'slug: латиница, цифры и дефис'),
    name: z.string().min(1).max(80),
    shortName: z.string().min(1).max(24).optional(),
    tagline: z.string().max(160).optional(),
    description: z.string().max(1200).optional(),
    isDemo: z.boolean().default(false),
    locale: z.string().default('ru-RU'),
    timezone: z.string().refine((tz) => {
      try {
        new Intl.DateTimeFormat('en', { timeZone: tz });
        return true;
      } catch {
        return false;
      }
    }, 'неизвестный часовой пояс'),
    currency: z.string().regex(/^[A-Z]{3}$/).default('RUB'),
    contacts: z.object({
      phone: z.string().min(5).max(32),
      email: z.string().email().max(120).optional(),
      address: z.string().min(3).max(240),
      addressHint: z.string().max(400).optional(),
      mapUrl: z.string().url().startsWith('https://').optional(),
      lat: z.number().min(-90).max(90).optional(),
      lng: z.number().min(-180).max(180).optional(),
    }),
    brand: z.object({
      accent: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default('#4690FF'),
      background: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default('#000000'),
      hero: imageRef,
      logo: imageRef.optional(),
      icon: imageRef.optional(),
    }),
    infoCards: z
      .array(z.object({ icon: z.string().max(30).optional(), title: z.string().min(1).max(40), text: z.string().max(160) }))
      .max(3)
      .default([]),
    assistantExamples: z.array(z.string().min(2).max(60)).max(4).default([]),
    booking: z
      .object({
        slotStepMin: z.union([z.literal(10), z.literal(15), z.literal(20), z.literal(30), z.literal(60)]).default(30),
        minLeadMin: z.number().int().min(0).max(2880).default(60),
        horizonDays: z.number().int().min(1).max(180).default(30),
        cancelPolicyHours: z.number().int().min(0).max(168).default(12),
        cancelPolicyText: z.string().max(400).optional(),
      })
      .default({ slotStepMin: 30, minLeadMin: 60, horizonDays: 30, cancelPolicyHours: 12 }),
    bays: z.array(z.object({ key, name: z.string().min(1).max(60), description: z.string().max(240).optional() })).min(1).max(30),
    services: z
      .array(
        z.object({
          key,
          name: z.string().min(1).max(80),
          category: z.string().max(40).optional(),
          description: z.string().max(600).optional(),
          price: z.number().min(0).max(10_000_000),
          priceFrom: z.boolean().default(false),
          durationMin: z.number().int().min(10).max(20160),
          bufferMin: z.number().int().min(0).max(1440).default(0),
          bays: z.array(key).optional(),
        }),
      )
      .min(1)
      .max(80),
    hours: z.object(Object.fromEntries(WEEKDAYS.map((d) => [d, dayHours])) as Record<(typeof WEEKDAYS)[number], typeof dayHours>),
    exceptions: z
      .array(
        z.object({
          date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
          closed: z.boolean().default(true),
          hours: range.optional(),
          note: z.string().max(120).optional(),
        }),
      )
      .default([]),
    gallery: z.array(z.object({ key, image: imageRef, caption: z.string().max(140).optional() })).max(40).default([]),
    owner: z.object({ email: z.string().email() }).optional(),
  })
  .superRefine((b, ctx) => {
    const bayKeys = new Set(b.bays.map((x) => x.key));
    if (bayKeys.size !== b.bays.length) ctx.addIssue({ code: 'custom', path: ['bays'], message: 'ключи боксов повторяются' });
    const svcKeys = new Set(b.services.map((x) => x.key));
    if (svcKeys.size !== b.services.length) ctx.addIssue({ code: 'custom', path: ['services'], message: 'ключи услуг повторяются' });
    b.services.forEach((s, i) =>
      s.bays?.forEach((k) => {
        if (!bayKeys.has(k)) ctx.addIssue({ code: 'custom', path: ['services', i, 'bays'], message: `нет бокса «${k}»` });
      }),
    );
    const gKeys = new Set(b.gallery.map((x) => x.key));
    if (gKeys.size !== b.gallery.length) ctx.addIssue({ code: 'custom', path: ['gallery'], message: 'ключи фото повторяются' });
    if (!WEEKDAYS.some((d) => b.hours[d] !== null)) ctx.addIssue({ code: 'custom', path: ['hours'], message: 'нет ни одного рабочего дня' });
    b.exceptions.forEach((e, i) => {
      if (!e.closed && !e.hours) ctx.addIssue({ code: 'custom', path: ['exceptions', i, 'hours'], message: 'для рабочего дня укажите часы' });
    });
  });

export type Business = z.infer<typeof businessSchema>;

/** Weekly hours -> rows for the database (ISO weekday 1..7). */
export function hoursToRows(hours: Business['hours']) {
  const rows: { weekday: number; opens: string; closes: string }[] = [];
  WEEKDAYS.forEach((d, i) => {
    const v = hours[d];
    const list = v === null ? [] : Array.isArray(v) ? v : [v];
    for (const r of list) {
      const [opens, closes] = r.split('-');
      rows.push({ weekday: i + 1, opens, closes });
    }
  });
  return rows;
}

/** Owner/client facing input validation (Zod for data people type in). */
export const phoneInput = z
  .string()
  .trim()
  .transform((v) => v.replace(/[^\d+]/g, ''))
  .refine((v) => {
    const d = v.replace(/\D/g, '');
    return d.length >= 10 && d.length <= 15;
  }, 'Введите номер полностью, например +7 900 123-45-67');

export const bookingFormSchema = z.object({
  name: z.string().trim().min(1, 'Как к вам обращаться?').max(80),
  phone: phoneInput,
  car: z.string().trim().min(2, 'Укажите марку и модель').max(80),
  comment: z.string().trim().max(500, 'Не больше 500 символов').optional(),
});
export type BookingForm = z.infer<typeof bookingFormSchema>;

export const serviceFormSchema = z.object({
  name: z.string().trim().min(1, 'Название обязательно').max(80),
  category: z.string().trim().max(40).optional(),
  description: z.string().trim().max(600).optional(),
  price: z.coerce.number().min(0, 'Цена не может быть отрицательной').max(10_000_000),
  price_from: z.boolean(),
  duration_min: z.coerce.number().int().min(10, 'Минимум 10 минут').max(20160, 'Максимум 14 дней'),
  buffer_min: z.coerce.number().int().min(0).max(1440),
  is_active: z.boolean(),
  resource_ids: z.array(z.string()),
});

export const profileFormSchema = z.object({
  name: z.string().trim().min(1, 'Название обязательно').max(80),
  tagline: z.string().trim().max(160),
  description: z.string().trim().max(1200),
  address: z.string().trim().min(3, 'Укажите адрес').max(240),
  address_hint: z.string().trim().max(400),
  map_url: z.union([z.literal(''), z.string().trim().url('Ссылка должна начинаться с https://').startsWith('https://')]),
  phone: z.string().trim().min(5, 'Укажите телефон').max(32),
  cancel_policy_hours: z.coerce.number().int().min(0).max(168),
});

export const infoCardSchema = z.object({
  icon: z.string().max(30).optional(),
  title: z.string().trim().min(1, 'Заголовок обязателен').max(40),
  text: z.string().trim().max(160),
});
